/**
 * useRealtimeStats
 *
 * Polls the realtime-events endpoint every 30s for live visitor counts and the
 * last event timestamp. Drives the dashboard "is the product alive?" ribbon —
 * Verna's engagement signal that makes the user refresh tomorrow.
 *
 * Defensively parses the response shape (the endpoint returns either an
 * `activeUsers` count + recent events list or a wrapped object). Falls back to
 * zeros silently — this is a status indicator, not a critical path.
 */

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getRealtimeEvents } from '@/lib/api/api'

interface RealtimeStats {
  liveVisitors: number
  lastEventAt: string | null
  isLoading: boolean
}

function parseLiveVisitors(raw: any): number {
  if (!raw) return 0
  if (typeof raw.activeUsers === 'number') return raw.activeUsers
  if (typeof raw.liveVisitors === 'number') return raw.liveVisitors
  if (typeof raw.active === 'number') return raw.active
  if (Array.isArray(raw.sessions)) return raw.sessions.length
  if (Array.isArray(raw.activeSessions)) return raw.activeSessions.length
  return 0
}

function parseLastEvent(raw: any): string | null {
  const events: any[] =
    raw?.events ?? raw?.recentEvents ?? raw?.recent ?? []
  if (!Array.isArray(events) || events.length === 0) return null
  const first = events[0]
  return first?.timestamp ?? first?.time ?? first?.createdAt ?? null
}

export function useRealtimeStats(
  propertyId: string | null | undefined,
): RealtimeStats {
  const { token } = useAuth()
  const enabled = !!propertyId && !!token

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-realtime', propertyId],
    queryFn: async () => {
      if (!propertyId || !token) return null
      try {
        return await getRealtimeEvents(propertyId, token)
      } catch {
        return null
      }
    },
    enabled,
    refetchInterval: 30_000,
    staleTime: 0,
    retry: false,
  })

  return {
    liveVisitors: parseLiveVisitors(data),
    lastEventAt: parseLastEvent(data),
    isLoading: enabled && isLoading,
  }
}

/** Format an ISO timestamp as a compact "12s" / "4m" / "2h" / "3d" relative label. */
function formatTimeAgo(timestamp: string | null | undefined): string | null {
  if (!timestamp) return null
  const then = new Date(timestamp).getTime()
  if (!Number.isFinite(then)) return null
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}
